import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { LocationModal } from './LocationModal';
import { toast } from 'sonner';
import { useTranslation } from 'react-i18next';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

/**
 * LocationModalWrapper - Manages the location modal display logic
 * Shows the modal after login (not registration) if user has no city set
 * New registrations are handled by WelcomeModal in BeautifulHomePage
 */
export const LocationModalWrapper = ({ children }) => {
  const { t } = useTranslation();
  const { user, isAuthenticated, updateUser } = useAuth();
  const [showLocationModal, setShowLocationModal] = useState(false);
  const [hasCheckedLocation, setHasCheckedLocation] = useState(false);

  // Check if we should show the location modal
  useEffect(() => {
    if (isAuthenticated && user && !hasCheckedLocation) {
      // Check if this is a brand new registration (created in last 10 seconds)
      // If so, skip - WelcomeModal will handle the flow
      const createdAt = user.created_at ? new Date(user.created_at) : null;
      const isNewRegistration = createdAt && (Date.now() - createdAt.getTime()) < 10000;
      
      if (isNewRegistration) {
        // Skip location modal for new registrations
        setHasCheckedLocation(true);
        return;
      }
      
      // Show modal if user has no city set AND hasn't been dismissed before
      if (!user.city) {
        const dismissed = localStorage.getItem('locationModalDismissed');

        if (dismissed) {
          setHasCheckedLocation(true);
          return;
        }

        // Small delay to let auth complete smoothly
        const timer = setTimeout(() => {
          setShowLocationModal(true);
          localStorage.setItem('locationModalDismissed', 'true');
          setHasCheckedLocation(true);
        }, 500);
        return () => clearTimeout(timer);
      } else {
        // User already has a city, mark as checked
        setHasCheckedLocation(true);
      }
    }
    
    // Reset check when user logs out
    if (!isAuthenticated && hasCheckedLocation) {
      setHasCheckedLocation(false);
    }
  }, [isAuthenticated, user, hasCheckedLocation]);

  // Handle saving the city
  const handleSaveCity = async (city) => {
    try {
      const response = await axios.put(`${API}/user/city`, { city });
      
      // Update local user state
      updateUser({ city });
      
      setHasCheckedLocation(true);
      localStorage.removeItem('locationModalDismissed');
      toast.success(t('cityUpdatedSuccessfully'));
    } catch (error) {
      console.error('Failed to save city:', error);
      toast.error(t('failedToSaveLocation'));
      throw error;
    }
  };

  // Handle skipping the modal
  const handleSkip = () => {
    setHasCheckedLocation(true);
    // Don't permanently disable - will show again on next login
  };

  return (
    <>
      {children}
      <LocationModal
        open={showLocationModal}
        onOpenChange={setShowLocationModal}
        onSave={handleSaveCity}
        onSkip={handleSkip}
      />
    </>
  );
};

export default LocationModalWrapper;
